import { Button } from "@studyplus/boron-ui";
import { FormikHelpers, useFormik } from "formik";
import { FC, useCallback, useEffect, useState } from "react";
import Input from "../../../components/atoms/Input";
import Label from "../../../components/atoms/Label";
import ResumableTextarea from "../../../components/atoms/ResumableTextarea";
import {
  generateStorageKey,
  useResumableTextarea,
} from "../../../components/atoms/ResumableTextarea/useResumableTextarea";
import { UnprocessableEntityError } from "../../../errors";
import { WithRouterProps, withRouter } from "../../../helpers/RouterHelper";
import { AuthenticateRouterProps } from "../../../hocs/enhanceAuthenticatedPage";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
import { ApiErrorInterface } from "../../../interfaces/ApiErrorResponseInterface";
import KarteInterface, {
  KarteAttachment,
  KarteImageAttachment,
} from "../../../interfaces/KarteInterface";
import StudentInterface from "../../../interfaces/StudentInterface";
import { ApiErrors } from "./ApiErrors";
import { ExistingFiles, ExistingImages } from "./ExistingAttachments";
import { FileInputIcon } from "./FileInputIcon";
import { FileNames } from "./FileNames";
import { CommentError, StaffNameError } from "./FormErrors";
import { ImageInputIcon } from "./ImageInputIcon";
import { ImageNames } from "./ImageNames";
import styles from "./styles.scss";
import {
  UpdateKarteParams,
  useUpdateStudentKarte,
} from "./useUpdateStudentKarte";
import validationSchema from "./validationSchema";

export interface Props extends WithRouterProps<AuthenticateRouterProps> {
  student: StudentInterface;
  karte: KarteInterface;
}

interface KarteAttachmentValue extends KarteAttachment {
  shouldBeDeleted: boolean;
}

interface KarteImageAttachmentValue extends KarteImageAttachment {
  shouldBeDeleted: boolean;
}

export interface Values {
  name: string;
  comment: string;
  existingImageAttachments: KarteImageAttachmentValue[];
  existingFileAttachments: KarteAttachmentValue[];
  karteImageAttachments?: File[];
  karteFileAttachments?: File[];
}

const StudentKarteEditForm: FC<Props> = (props) => {
  const [apiErrors, setApiErrors] = useState<ApiErrorInterface[]>([]);

  const { showSuccessMessage, showErrorMessage } = useFlashMessage();

  const values = mapPropsToValues(props);

  const { clearValue: clearCommentStorage, ...resumableTextareaProps } =
    useResumableTextarea({
      value: values.comment,
      key: generateStorageKey(["kartes", props.student.id, props.karte.id]),
      confirmMessage:
        "編集途中のカルテを復元しますか？\n（「キャンセル」を選択すると復元内容が破棄されます。）",
    });

  const { mutate: updateKarte } = useUpdateStudentKarte({
    studentId: props.student.id,
    karteId: props.karte.id,
  });

  const handleSubmit = useCallback(
    (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
      const { student, navigate, location } = props;
      clearCommentStorage();

      updateKarte(valuesToParams(values), {
        onSettled: () => {
          setSubmitting(false);
        },
        onSuccess: () => {
          showSuccessMessage("カルテを更新しました");

          // 遷移元のページをbacktoから判断して戻る
          // 指定がなければ生徒カルテ一覧に戻る
          const query = new URLSearchParams(location.search);
          const backto = query.get("backto") ?? "sections";
          if (backto === "sections") {
            navigate(`/sections/${student.section.id}/kartes`);
          } else {
            navigate(`/students/${student.id}/kartes`);
          }
        },
        onError: (error) => {
          showErrorMessage("カルテを更新できませんでした");
          if (error instanceof UnprocessableEntityError) {
            setApiErrors(error.originalErrors);
          }
        },
      });
    },
    [
      props.student,
      props.navigate,
      clearCommentStorage,
      props.location,
      updateKarte,
      showSuccessMessage,
      showErrorMessage,
    ],
  );

  const formik = useFormik<Values>({
    initialValues: values,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const setExistingImageAttachments = useCallback(
    (existingImageAttachments: Values["existingImageAttachments"]) => {
      formik.setFieldValue(
        "existingImageAttachments",
        existingImageAttachments,
      );
    },
    [formik.setFieldValue],
  );

  const setExistingFileAttachments = useCallback(
    (existingFileAttachments: Values["existingFileAttachments"]) => {
      formik.setFieldValue("existingFileAttachments", existingFileAttachments);
    },
    [formik.setFieldValue],
  );

  const setKarteImageAttachments = useCallback(
    (karteImageAttachments: Values["karteImageAttachments"]) => {
      formik.setFieldValue("karteImageAttachments", karteImageAttachments);
    },
    [formik.setFieldValue],
  );

  const setKarteFileAttachments = useCallback(
    (karteFileAttachments: Values["karteFileAttachments"]) => {
      formik.setFieldValue("karteFileAttachments", karteFileAttachments);
    },
    [formik.setFieldValue],
  );

  useEffect(() => {
    const { isResumed, resumedValue } = resumableTextareaProps;
    if (isResumed && resumedValue) {
      formik.getFieldHelpers("comment").setValue(resumedValue);
    }
  }, [resumableTextareaProps.isResumed, resumableTextareaProps.resumedValue]);

  return (
    <div className={styles.root}>
      <form className={styles.form} onSubmit={formik.handleSubmit}>
        <div className={styles.header}>
          <div className={styles.staffName}>
            <Label
              htmlFor={"StudentKarteForm-staffName-input"}
              isMute={true}
              isInline={true}
            >
              スタッフ名
            </Label>
            <StaffNameError
              touched={formik.touched.name}
              error={formik.errors.name}
            />
            <Input
              id="StudentKarteForm-staffName-input"
              name="name"
              hasError={formik.touched.name && !!formik.errors.name}
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.comment}>
            <Label
              htmlFor={"StudentKarteForm-comment-textarea"}
              isInline={true}
            >
              コメント
            </Label>
            <CommentError
              touched={formik.touched.comment}
              error={formik.errors.comment}
            />
            <ResumableTextarea
              id="StudentKarteForm-comment-textarea"
              name="comment"
              hasError={formik.touched.comment && !!formik.errors.comment}
              rows={3}
              placeholder="コメントを入力"
              value={formik.values.comment}
              onChange={formik.handleChange}
              {...resumableTextareaProps}
            />
          </div>
        </div>

        <ExistingImages
          existingImageAttachments={formik.values.existingImageAttachments}
          setValue={setExistingImageAttachments}
        />
        <ExistingFiles
          existingFileAttachments={formik.values.existingFileAttachments}
          setValue={setExistingFileAttachments}
        />
        <ApiErrors errors={apiErrors} />

        <div className={styles.footer}>
          {props.student.canCreateKarteAttachment && (
            <>
              <div className={styles.attachments}>
                <ImageNames
                  karteImageAttachments={formik.values.karteImageAttachments}
                  setKarteImageAttachments={setKarteImageAttachments}
                />
                <FileNames
                  karteFileAttachments={formik.values.karteFileAttachments}
                  setKarteFileAttachments={setKarteFileAttachments}
                />
              </div>
              <ImageInputIcon
                karteImageAttachments={formik.values.karteImageAttachments}
                setKarteImageAttachments={setKarteImageAttachments}
              />
              <FileInputIcon
                karteFileAttachments={formik.values.karteFileAttachments}
                setKarteFileAttachments={setKarteFileAttachments}
              />
            </>
          )}
          <Button type="submit" disabled={formik.isSubmitting}>
            登録
          </Button>
        </div>
      </form>
    </div>
  );
};

const valuesToParams = (values: Values): UpdateKarteParams => {
  const params: UpdateKarteParams = {
    name: values.name,
    comment: values.comment,
  };

  // 画像添付: 削除分
  const imageDelete = values.existingImageAttachments
    .filter((a) => a.shouldBeDeleted)
    .map((a) => ({ id: a.id, _destroy: "true" as const }));
  // 画像添付: 追加分
  const imageAdd =
    values.karteImageAttachments?.map((file) => ({ file })) ?? [];
  const imageAll = [...imageDelete, ...imageAdd];
  if (imageAll.length > 0) {
    params.karte_image_attachments_attributes = imageAll;
  }

  // ファイル添付: 削除分
  const fileDelete = values.existingFileAttachments
    .filter((a) => a.shouldBeDeleted)
    .map((a) => ({ id: a.id, _destroy: "true" as const }));
  // ファイル添付: 追加分
  const fileAdd = values.karteFileAttachments?.map((file) => ({ file })) ?? [];
  const fileAll = [...fileDelete, ...fileAdd];
  if (fileAll.length > 0) {
    params.karte_attachments_attributes = fileAll;
  }

  return params;
};

const mapPropsToValues = (props: Props): Values => {
  return {
    name: props.karte.staffName,
    comment: props.karte.comment,
    existingImageAttachments: props.karte.imageAttachments.map(
      (imageAttachment) => ({ ...imageAttachment, shouldBeDeleted: false }),
    ),
    existingFileAttachments: props.karte.fileAttachments.map(
      (fileAttachment) => ({
        ...fileAttachment,
        shouldBeDeleted: false,
      }),
    ),
  };
};

export default withRouter<Props>(StudentKarteEditForm);
